import pb, { requireAuth } from '../pocketbaseClient';

class QuizTemplateService {
    constructor() {
        this.abortController = null;
    }

    // Get all quiz templates
    async getTemplates() {
        try {
            requireAuth();
            // Cancel any existing request
            if (this.abortController) {
                this.abortController.abort();
            }
            
            // Create new abort controller
            this.abortController = new AbortController();
            
            const records = await pb.collection('quiz_templates').getFullList({
                sort: '-created',
                $autoCancel: false // Disable auto-cancellation
            });
            
            // Handle quiz data for each record
            return records.map(record => {
                let quizData = record.quiz;
                
                // If it's a string, try to parse it
                if (typeof quizData === 'string') {
                    try {
                        quizData = JSON.parse(quizData);
                    } catch (parseError) {
                        console.error(`Error parsing quiz data for template ${record.id}:`, parseError);
                        console.log('Raw quiz data:', quizData);
                        // Use fallback for this specific record
                        quizData = {
                            title: '',
                            description: '',
                            conclusionText: '',
                            questions: []
                        };
                    }
                }
                
                // Ensure quizData has the expected structure
                if (!quizData || typeof quizData !== 'object') {
                    quizData = {
                        title: '',
                        description: '',
                        conclusionText: '',
                        questions: []
                    };
                }
                
                return {
                    ...record,
                    quiz: quizData
                };
            });
        } catch (error) {
            // Don't log error if it's just a cancellation
            if (error.name !== 'AbortError') {
                // If collection doesn't exist, return empty array instead of throwing
                if (error.status === 404 || error.message?.includes('collection') || error.message?.includes('not found')) {
                    console.warn('Quiz templates collection not found, returning empty array');
                    return [];
                }
                console.error('Error fetching quiz templates:', error);
            }
            throw error;
        }
    }

    // Create a new quiz template
    async createTemplate(templateData) {
        try {
            requireAuth();
            const data = {
                name: templateData.name,
                quiz: typeof templateData.quiz === 'string' ? templateData.quiz : JSON.stringify(templateData.quiz)
            };
            
            const record = await pb.collection('quiz_templates').create(data);
            return record;
        } catch (error) {
            console.error('Error creating quiz template:', error);
            throw error;
        }
    }

    // Update an existing quiz template
    async updateTemplate(templateId, templateData) {
        try {
            requireAuth();
            const data = {
                name: templateData.name,
                quiz: JSON.stringify(templateData.quiz)
            };
            
            const record = await pb.collection('quiz_templates').update(templateId, data);
            return record;
        } catch (error) {
            console.error('Error updating quiz template:', error);
            throw error;
        }
    }

    // Delete a quiz template
    async deleteTemplate(templateId) {
        try {
            requireAuth();
            await pb.collection('quiz_templates').delete(templateId);
            return true;
        } catch (error) {
            console.error('Error deleting quiz template:', error);
            throw error;
        }
    }

    // Get a specific quiz template by ID
    async getTemplate(templateId) {
        try {
            requireAuth();
            const record = await pb.collection('quiz_templates').getOne(templateId);
            
            // Debug: Log the raw record to see what we're getting
            console.log('Raw record from PocketBase:', record);
            console.log('Quiz field type:', typeof record.quiz);
            console.log('Quiz field value:', record.quiz);
            
            // Handle quiz data - it might already be parsed or be a string
            let quizData = record.quiz;
            
            // If it's a string, try to parse it
            if (typeof quizData === 'string') {
                try {
                    quizData = JSON.parse(quizData);
                } catch (parseError) {
                    console.error('Error parsing quiz data:', parseError);
                    console.log('Raw quiz data:', quizData);
                    throw new Error(`Invalid JSON in quiz template: ${parseError.message}`);
                }
            }
            
            // Ensure quizData has the expected structure
            if (!quizData || typeof quizData !== 'object') {
                console.warn('Quiz data is not an object, using fallback');
                quizData = {
                    title: '',
                    description: '',
                    conclusionText: '',
                    questions: []
                };
            }
            
            return {
                ...record,
                quiz: quizData
            };
        } catch (error) {
            console.error('Error fetching quiz template:', error);
            throw error;
        }
    }

    // Cleanup method to cancel any pending requests
    cleanup() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
}

const quizTemplateService = new QuizTemplateService();
export default quizTemplateService;
